import { useMemo } from 'react';
import { ChevronDown, Layers, Wallet, User } from 'lucide-react';
import clsx from 'clsx';
import FinApartmentTable from './FinApartmentTable';
import { getApartmentsByBlockId } from '@mocks/daireler';

/**
 * FinBlockAccordion — Blok Seviyesi Finansal Özet
 * 
 * Blok bazlı toplam satış, tahsilat ve alacak durumunu gösterir.
 * Genişletildiğinde daire listesini tablo olarak sunar.
 */
export default function FinBlockAccordion({
    blok, insaatId, isExpanded, onToggle,
    formatCurrency, filterDurum, searchTerm
}) {
    // Blok verilerini hesapla
    const daireler = useMemo(() => getApartmentsByBlockId(blok.id), [blok.id]);

    const stats = useMemo(() => {
        const soldApartments = daireler.filter(d => d.status === 'sold' || d.status === 'reserved');
        return {
            totalDaire: daireler.length,
            satilanDaire: soldApartments.length,
            toplamSatis: soldApartments.reduce((acc, d) => acc + d.totalPrice, 0),
            toplamTahsilat: soldApartments.reduce((acc, d) => acc + d.paidAmount, 0),
            kalanAlacak: soldApartments.reduce((acc, d) => acc + d.remainingAmount, 0),
        };
    }, [daireler]);

    const tahsilatOrani = stats.toplamSatis > 0
        ? Math.round((stats.toplamTahsilat / stats.toplamSatis) * 100)
        : 0;

    const getProgressColor = (y) => y >= 75 ? 'bg-success' : y >= 40 ? 'bg-primary' : y > 0 ? 'bg-warning' : 'bg-bg-tertiary';
    const getTextColor = (y) => y >= 75 ? 'text-success' : y >= 40 ? 'text-primary' : y > 0 ? 'text-warning' : 'text-text-muted';

    return (
        <div className="bg-bg-primary rounded-xl border border-border-light overflow-hidden hover:border-primary/30 transition-colors">
            {/* Blok Header */}
            <button
                onClick={onToggle}
                className="w-full flex items-center gap-4 p-4 text-left"
            >
                {/* İkon */}
                <div className="hidden sm:flex w-10 h-10 rounded-lg bg-bg-tertiary items-center justify-center shrink-0">
                    <Layers size={20} className="text-text-secondary" />
                </div>

                {/* Blok Bilgisi */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-semibold text-text-primary">{blok.name}</span>
                        <span className="text-xs text-text-muted">({stats.totalDaire} Daire)</span>
                    </div>

                    {/* Progress Bar (Tahsilat) */}
                    <div className="flex items-center gap-2 max-w-[200px]">
                        <div className="h-1.5 flex-1 bg-bg-tertiary rounded-full overflow-hidden">
                            <div className={clsx('h-full rounded-full', getProgressColor(tahsilatOrani))} style={{ width: `${tahsilatOrani}%` }} />
                        </div>
                        <div className={clsx('text-xs font-bold w-8 text-right', getTextColor(tahsilatOrani))}>%{tahsilatOrani}</div>
                    </div>
                </div>

                {/* Finansal Özet */}
                <div className="flex flex-col items-end gap-0.5 text-right mr-2">
                    <div className="text-sm font-semibold text-primary">{formatCurrency(stats.toplamSatis)}</div>
                    <div className="text-xs text-text-muted">
                        <span className="text-success">{formatCurrency(stats.toplamTahsilat)}</span> / <span className="text-warning">{formatCurrency(stats.kalanAlacak)}</span>
                    </div>
                </div>

                <ChevronDown size={18} className={clsx('text-text-muted transition-transform duration-300', isExpanded && 'rotate-180')} />
            </button>

            {/* Daire Tablosu */}
            {isExpanded && (
                <div className="border-t border-border-light">
                    <FinApartmentTable
                        daireler={daireler}
                        formatCurrency={formatCurrency}
                        filterDurum={filterDurum}
                        searchTerm={searchTerm}
                    />
                </div>
            )}
        </div>
    );
}
